const BASE = '/api';
const TOKEN_KEY = 'glowlytix_token';

export function getToken() { return localStorage.getItem(TOKEN_KEY); }
export function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
export function clearToken() { localStorage.removeItem(TOKEN_KEY); }

function authHeaders(extra = {}) {
  const h = { 'Content-Type': 'application/json', ...extra };
  const token = getToken();
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: authHeaders(options.headers),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ─── Auth ────────────────────────────────────────────────────────────────────
export async function apiRegister(email, password) {
  const data = await request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (data.token) setToken(data.token);
  return data;
}

export async function apiLogin(email, password) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (data.token) setToken(data.token);
  return data;
}

export async function apiMe() {
  const data = await request('/auth/me');
  if (data.token) setToken(data.token); // persist the refreshed token
  return data;
}

// ─── Entries ─────────────────────────────────────────────────────────────────
export async function apiGetEntries() {
  return request('/entries');
}

export async function apiCreateEntry(entry) {
  return request('/entries', { method: 'POST', body: JSON.stringify(entry) });
}

export async function apiUpdateEntry(id, entry) {
  return request(`/entries/${id}`, { method: 'PUT', body: JSON.stringify(entry) });
}

export async function apiDeleteEntry(id) {
  return request(`/entries/${id}`, { method: 'DELETE' });
}

// ─── Routines ────────────────────────────────────────────────────────────────
export async function apiGetRoutine() {
  return request('/routines');
}

export async function apiSaveRoutine(routines) {
  return request('/routines', { method: 'PUT', body: JSON.stringify(routines) });
}

// ─── Insights ────────────────────────────────────────────────────────────────
export async function apiGetInsights() {
  return request('/insights');
}

export async function apiGenerateInsight(entries) {
  return request('/insights/generate', { method: 'POST', body: JSON.stringify({ entries }) });
}

export async function apiDeleteInsight(id) {
  return request(`/insights/${id}`, { method: 'DELETE' });
}

export async function apiAnalyzePhoto(photoUrl, context) {
  return request('/insights/analyze-photo', {
    method: 'POST',
    body: JSON.stringify({ photoUrl, context }),
  });
}

// ─── Account / GDPR ──────────────────────────────────────────────────────────
export async function apiExportData() {
  const token = getToken();
  const res = await fetch('/api/auth/export', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

export async function apiDeleteAccount() {
  return request('/auth/account', { method: 'DELETE' });
}

// ─── Stripe ──────────────────────────────────────────────────────────────────
export async function apiCreateCheckoutSession(planId) {
  return request('/stripe/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ planId }),
  });
}

export async function apiCreatePortalSession() {
  return request('/stripe/create-portal-session', { method: 'POST' });
}
